/**
 * LNV Wishlist
 * Version: 1.2
 *
 * Browser-based product Save system.
 * Storage: localStorage
 *
 * v1.2:
 * - Keeps real Horizon product titles
 * - Extracts only the customer-visible selling price
 * - Excludes Horizon accessibility labels such as
 *   "Sale price" and "Regular price"
 * - Repairs metadata for already-saved products
 * - Preserves lnv:wishlist:v1 storage
 * - No Shopify core-file modification
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'lnv:wishlist:v1';
  const MAX_ITEMS = 100;

  let wishlist = readWishlist();
  let scheduled = false;


  /* =======================================================
     STORAGE
     ======================================================= */

  function readWishlist() {
    try {
      const stored =
        window.localStorage.getItem(
          STORAGE_KEY
        );

      if (!stored) {
        return [];
      }

      const parsed =
        JSON.parse(stored);

      return Array.isArray(parsed)
        ? parsed.slice(0, MAX_ITEMS)
        : [];

    } catch (error) {
      return [];
    }
  }


  function writeWishlist() {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(
          wishlist.slice(
            0,
            MAX_ITEMS
          )
        )
      );

    } catch (error) {
      /*
       * Storage unavailable.
       * Keep storefront functional.
       */
    }
  }


  function findSavedIndex(productId) {
    return wishlist.findIndex(
      function (item) {
        return (
          String(item.id) ===
          String(productId)
        );
      }
    );
  }


  function isSaved(productId) {
    return (
      findSavedIndex(productId) !== -1
    );
  }


  /* =======================================================
     TEXT HELPERS
     ======================================================= */

  function cleanText(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim();
  }


  /*
   * Horizon includes screen-reader labels such as:
   *
   * "Sale price"
   * "Regular price"
   *
   * inside .visually-hidden elements.
   *
   * Those labels are correct for accessibility on the
   * original product card, but they should not be stored
   * as the visual Wishlist price.
   */

  function getVisibleText(element) {
    if (!element) {
      return '';
    }

    const clone =
      element.cloneNode(true);

    clone
      .querySelectorAll(
        '.visually-hidden'
      )
      .forEach(
        function (hidden) {
          hidden.remove();
        }
      );

    return cleanText(
      clone.textContent
    );
  }


  /* =======================================================
     PRODUCT PRICE
     ======================================================= */

  function getProductPrice(card) {
    /*
     * Horizon current selling price:
     *
     * Sale:
     * .price-item__group.price
     *
     * Normal:
     * .price
     *
     * We intentionally store only the active selling
     * price for the lightweight Wishlist card.
     */

    const priceElement =
      card.querySelector(
        'product-price ' +
        '.price-item__group.price'
      ) ||
      card.querySelector(
        'product-price .price'
      );

    return getVisibleText(
      priceElement
    );
  }


  /* =======================================================
     PRODUCT DATA
     ======================================================= */

  function getProductData(card) {
    const productId =
      card.dataset.productId;

    const productLink =
      card.querySelector(
        '.product-card__link[href]'
      );

    if (
      !productId ||
      !productLink
    ) {
      return null;
    }


    /*
     * Horizon places product.title in the
     * overlay product-card link aria-label.
     *
     * Visible title block remains a fallback.
     */

    const titleBlock =
      card.querySelector(
        '.product-card__content > ' +
        'a.contents.user-select-text ' +
        '.text-block'
      ) ||
      card.querySelector(
        '.product-card__content ' +
        '.text-block'
      );


    const title =
      cleanText(
        productLink.getAttribute(
          'aria-label'
        )
      ) ||
      cleanText(
        titleBlock
          ? titleBlock.textContent
          : ''
      ) ||
      'Product';


    const image =
      card.querySelector(
        '.card-gallery img'
      );


    return {
      id: String(productId),

      title: title,

      url: productLink.href,

      image: image
        ? (
            image.currentSrc ||
            image.src ||
            ''
          )
        : '',

      price:
        getProductPrice(card)
    };
  }


  /* =======================================================
     UPDATE ALREADY-SAVED PRODUCT DATA
     ======================================================= */

  function refreshSavedProduct(product) {
    const index =
      findSavedIndex(product.id);

    if (index === -1) {
      return;
    }


    const current =
      wishlist[index];


    const changed =
      current.title !== product.title ||
      current.url !== product.url ||
      current.image !== product.image ||
      current.price !== product.price;


    if (!changed) {
      return;
    }


    wishlist[index] = product;

    writeWishlist();
  }


  /* =======================================================
     HEART ICON
     ======================================================= */

  function createHeartIcon() {
    const svg =
      document.createElementNS(
        'http://www.w3.org/2000/svg',
        'svg'
      );


    svg.setAttribute(
      'viewBox',
      '0 0 24 24'
    );


    svg.setAttribute(
      'aria-hidden',
      'true'
    );


    svg.setAttribute(
      'focusable',
      'false'
    );


    const path =
      document.createElementNS(
        'http://www.w3.org/2000/svg',
        'path'
      );


    path.setAttribute(
      'd',
      'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z'
    );


    svg.appendChild(path);

    return svg;
  }


  /* =======================================================
     BUTTON STATE
     ======================================================= */

  function updateButton(
    button,
    product
  ) {
    const saved =
      isSaved(product.id);


    button.setAttribute(
      'aria-pressed',
      saved ? 'true' : 'false'
    );


    button.setAttribute(
      'aria-label',
      saved
        ? 'Remove ' +
          product.title +
          ' from wishlist'
        : 'Save ' +
          product.title +
          ' to wishlist'
    );


    button.title =
      saved
        ? 'Remove from wishlist'
        : 'Save to wishlist';
  }


  /* =======================================================
     ADD / REMOVE
     ======================================================= */

  function toggleProduct(product) {
    const index =
      findSavedIndex(product.id);


    if (index !== -1) {

      wishlist.splice(
        index,
        1
      );

    } else {

      wishlist.unshift(
        product
      );


      wishlist =
        wishlist.slice(
          0,
          MAX_ITEMS
        );
    }


    writeWishlist();

    syncButtons();


    window.dispatchEvent(
      new CustomEvent(
        'lnv:wishlist:changed',
        {
          detail: {
            count:
              wishlist.length
          }
        }
      )
    );
  }


  /* =======================================================
     ENHANCE PRODUCT CARD
     ======================================================= */

  function enhanceCard(card) {
    if (
      card.dataset
        .lnvWishlistReady ===
        'true' ||
      card.hasAttribute(
        'data-placeholder'
      )
    ) {
      return;
    }


    const product =
      getProductData(card);


    if (!product) {
      return;
    }


    /*
     * Repairs previously stored title / image /
     * price metadata whenever the card is viewed.
     */

    refreshSavedProduct(
      product
    );


    const content =
      card.querySelector(
        '.product-card__content'
      );


    if (!content) {
      return;
    }


    card.dataset
      .lnvWishlistReady =
      'true';


    const button =
      document.createElement(
        'button'
      );


    button.type =
      'button';


    button.className =
      'lnv-wishlist-toggle';


    button.dataset.productId =
      product.id;


    button.appendChild(
      createHeartIcon()
    );


    updateButton(
      button,
      product
    );


    button.addEventListener(
      'click',
      function (event) {

        event.preventDefault();

        event.stopPropagation();

        toggleProduct(
          product
        );
      }
    );


    content.appendChild(
      button
    );
  }


  /* =======================================================
     FIND PRODUCT CARDS
     ======================================================= */

  function enhanceCards() {
    const cards =
      document.querySelectorAll(
        '.section-lnv-collection-header ' +
        '+ .shopify-section ' +
        'product-card[data-product-id]'
      );


    cards.forEach(
      enhanceCard
    );
  }


  /* =======================================================
     SYNC BUTTON STATES
     ======================================================= */

  function syncButtons() {
    const buttons =
      document.querySelectorAll(
        '.lnv-wishlist-toggle' +
        '[data-product-id]'
      );


    buttons.forEach(
      function (button) {

        const card =
          button.closest(
            'product-card'
          );


        if (!card) {
          return;
        }


        const product =
          getProductData(card);


        if (product) {

          updateButton(
            button,
            product
          );
        }
      }
    );
  }


  /* =======================================================
     SHOPIFY DYNAMIC GRID UPDATES
     ======================================================= */

  function scheduleEnhancement() {
    if (scheduled) {
      return;
    }


    scheduled = true;


    window.requestAnimationFrame(
      function () {

        scheduled = false;

        enhanceCards();
      }
    );
  }


  function startObserver() {
    const root =
      document.querySelector(
        'main'
      ) ||
      document.body;


    if (!root) {
      return;
    }


    const observer =
      new MutationObserver(
        function (mutations) {

          const hasAddedNodes =
            mutations.some(
              function (mutation) {

                return (
                  mutation
                    .addedNodes
                    .length > 0
                );
              }
            );


          if (hasAddedNodes) {

            scheduleEnhancement();
          }
        }
      );


    observer.observe(
      root,
      {
        childList: true,
        subtree: true
      }
    );
  }


  /* =======================================================
     INIT
     ======================================================= */

  function init() {
    enhanceCards();

    startObserver();
  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true
      }
    );

  } else {

    init();
  }


  /* =======================================================
     MULTI-TAB SYNC
     ======================================================= */

  window.addEventListener(
    'storage',
    function (event) {

      if (
        event.key !==
        STORAGE_KEY
      ) {
        return;
      }


      wishlist =
        readWishlist();


      syncButtons();
    }
  );

})();